/*
 * Lumos PDP: shared product page system.
 *
 * Scope: [data-lumos-pdp-root] (sections/lumos-pdp-main.liquid) and
 * [data-lumos-pdp-ymal] (sections/lumos-pdp-product-cards.liquid), used by
 * templates/product.helmets-v4-*.json. Nothing here touches other product
 * templates. Exposed as window.LumosPdp.
 *
 * Options: every product option is a group with a role (color, size, version,
 * other). The role only changes the styling, the summary order and the repair
 * priority; variant lookup and repair work over any number of groups.
 *
 * Gallery: owned by this file. Groups are keyed by every option value except
 * the size. When the key changes, the kept slide of the new group is
 * preloaded, then the old group is hidden (its Swiper destroyed) and the new
 * group shown with its Swiper (window.Swiper, loaded by the theme) moved to
 * the kept slide before paint. Without window.Swiper the native scroll-snap
 * fallback in assets/lumos-pdp.css is used.
 *
 * Cart: one POST to /cart/add.js with items[] (product + the setups the
 * shopper added with "Add setup") under the window.LumosCartOperations lock,
 * released before the theme's own `product:added` so CartSection
 * (assets/theme.js) re-renders and opens the drawer; Add to cart stays in its
 * Adding… state until the drawer is open. The accessory buttons in the media
 * column post straight away without opening the drawer (it would slide over
 * the purchase bar), lock the bar while they post, and join the bar total as
 * lines that are already in the cart.
 */
(() => {
  if (window.LumosPdp) return;

  const MOBILE_QUERY = '(max-width: 1000px)';
  const ROLE_ORDER = ['color', 'size', 'version', 'other'];
  const IMAGE_WAIT_MS = 4000;
  const DRAWER_WAIT_MS = 6000;
  /* Same Swiper options as the previous Ultra gallery (assets/ebike-version-gallery.js). */
  const SWIPER_OPTIONS = {
    desktop: { direction: 'horizontal', loop: false, centeredSlides: true, allowTouchMove: true },
    mobile: { direction: 'horizontal', loop: false, speed: 400, spaceBetween: 24, centeredSlides: true, allowTouchMove: true }
  };
  const products = new Map();
  const carousels = new Map();

  const toArray = (list) => Array.prototype.slice.call(list || []);
  const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  ));
  const shortSize = (value) => String(value || '').split(' (')[0].trim();
  const aliasList = (value) => String(value || '').split(',').map((part) => part.trim().toLowerCase()).filter(Boolean);
  const prefersReducedMotion = () => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const delimit = (precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const output = String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return delimit(0, ',', '.');
        case 'amount_with_comma_separator': return delimit(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return delimit(0, ' ', ',');
        case 'amount_with_space_separator': return delimit(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return delimit(2, ' ', '.');
        default: return delimit(2, ',', '.');
      }
    });
    return output.replace(/<[^>]*>/g, '');
  }

  function focusableWithin(element) {
    return toArray(element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])'
    )).filter((node) => node.offsetParent !== null || node === document.activeElement);
  }

  /* Short dates in the storefront language (document lang), e.g. "Oct 15". */
  function dateFormatter() {
    const options = { month: 'short', day: 'numeric' };
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', options);
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', options);
    }
  }

  class LumosPdpProduct {
    constructor(root) {
      this.root = root;
      this.ready = false;
      const dataElement = root.querySelector('[data-lumos-pdp-data]');
      try {
        this.data = JSON.parse(dataElement ? dataElement.textContent : '{}');
      } catch (error) {
        console.error('[lumos-pdp] Invalid product data', error);
        return;
      }
      if (!Array.isArray(this.data.variants) || this.data.variants.length === 0) return;

      this.labels = this.data.labels || {};
      this.valueLabels = this.data.valueLabels || {};
      this.options = (Array.isArray(this.data.options) ? this.data.options : []).map((option) => ({
        index: Number(option.index),
        name: option.name,
        role: ROLE_ORDER.indexOf(option.role) === -1 ? 'other' : option.role
      }));
      this.allIndexes = this.options.map((option) => option.index);
      /* Summary order and repair priority: color, size, version, then the other options in product order. */
      this.priority = this.options.slice().sort((a, b) => (
        (ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)) || (a.index - b.index)
      )).map((option) => option.index);
      this.primaryIndexes = this.options
        .filter((option) => option.role === 'color' || option.role === 'size')
        .map((option) => option.index);
      const sizeOption = this.options.find((option) => option.role === 'size');
      this.sizeIndex = sizeOption ? sizeOption.index : null;
      this.multiSetup = this.data.setupSelection === 'multi';

      this.form = root.querySelector('form.lumos-pdp__form');
      this.variantInput = root.querySelector('[data-lumos-pdp-variant-input]');
      this.bar = root.querySelector('[data-lumos-pdp-bar]');
      this.liveRegion = root.querySelector('[data-lumos-pdp-live]');
      this.mobileQuery = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : null;
      this.state = {
        setupIds: [],
        expandedId: null,
        quickPending: new Set(),
        quickAdded: new Set(),
        extras: new Map(),
        orderOpen: false,
        tab: 'photos',
        pending: false
      };
      this.gallery = { key: null, token: 0, surface: null };
      this.silent = false;
      this.openModalElement = null;
      this.modalTrigger = null;
      this.zoom = { images: [], index: 0 };
      this.scrollFrame = null;
      this.toastTimer = null;
      this.resizeTimer = null;
      this.quickTimers = new Map();

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = (event) => event.preventDefault();
      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onSurfaceChange = this.onSurfaceChange.bind(this);
      this.onHeaderTransitionEnd = this.onHeaderTransitionEnd.bind(this);
      this.onCartSettled = this.onCartSettled.bind(this);
      this.measure = this.measure.bind(this);
      this.onPageShow = (event) => {
        if (event.persisted) this.restoreFromCache();
      };

      this.variant = this.findVariant(this.selectedOptions())
        || this.data.variants.find((variant) => String(variant.id) === String(this.variantInput && this.variantInput.value))
        || this.data.variants[0];
      this.preferAvailableOnLoad();

      root.addEventListener('change', this.onChange);
      root.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('theme:loading:end', this.onCartSettled);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('load', this.measure);
      window.addEventListener('pageshow', this.onPageShow);
      if (this.mobileQuery) {
        if (typeof this.mobileQuery.addEventListener === 'function') this.mobileQuery.addEventListener('change', this.onSurfaceChange);
        else if (typeof this.mobileQuery.addListener === 'function') this.mobileQuery.addListener(this.onSurfaceChange);
      }
      if (typeof ResizeObserver === 'function' && this.bar) {
        this.barObserver = new ResizeObserver(() => this.measure());
        this.barObserver.observe(this.bar);
      }

      this.renderEta();
      this.render();
      this.watchBackInStock();
      this.measure();
      this.updateScrollState();
      this.initGallery();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure).catch(() => {});
      this.ready = true;
    }

    destroy() {
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('theme:loading:end', this.onCartSettled);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('load', this.measure);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.mobileQuery) {
        if (typeof this.mobileQuery.removeEventListener === 'function') this.mobileQuery.removeEventListener('change', this.onSurfaceChange);
        else if (typeof this.mobileQuery.removeListener === 'function') this.mobileQuery.removeListener(this.onSurfaceChange);
      }
      if (this.barObserver) this.barObserver.disconnect();
      if (this.backInStockObserver) this.backInStockObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.header) this.header.removeEventListener('transitionend', this.onHeaderTransitionEnd);
      this.releaseCartLock();
      this.gallery.token += 1;
      ['desktop', 'mobile'].forEach((surface) => {
        this.groups(surface).forEach((group) => this.destroySwiper(this.mainOf(group)));
      });
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.resizeTimer);
      this.quickTimers.forEach((timer) => window.clearTimeout(timer));
      this.quickTimers.clear();
      this.closeModal(false);
      document.body.classList.remove('lumos-pdp-has-bar');
    }

    /* ---------- money, labels ---------- */

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => { this.liveRegion.textContent = message; }, 30);
    }

    toast(message, isError) {
      const toast = this.root.querySelector('[data-lumos-pdp-toast]');
      if (!toast) return;
      window.clearTimeout(this.toastTimer);
      toast.textContent = message;
      toast.classList.toggle('is-error', Boolean(isError));
      toast.setAttribute('role', isError ? 'alert' : 'status');
      toast.hidden = false;
      this.toastTimer = window.setTimeout(() => { toast.hidden = true; }, isError ? 6000 : 3000);
    }

    /* ---------- variants ---------- */

    /* Selected value per option index (null when nothing is checked). */
    selectedOptions() {
      const selected = [];
      this.options.forEach((option) => {
        const input = this.root.querySelector(`input[data-lumos-pdp-option="${option.index}"]:checked`);
        selected[option.index] = input ? input.value : null;
      });
      return selected;
    }

    matches(variant, selected, indexes) {
      return indexes.every((index) => {
        const value = selected[index];
        if (value === null || value === undefined) return true;
        return variant.options[index] === value;
      });
    }

    findVariant(selected) {
      return this.data.variants.find((variant) => this.matches(variant, selected, this.allIndexes)) || null;
    }

    /* Keeps the option the shopper just picked and repairs the others to the
     * nearest variant: color outranks size, size outranks version, version
     * outranks other options, and an available variant wins a tie. So
     * Standard is never kept for sizes that only exist as Mips. */
    repairVariant(selected, changedIndex) {
      const hasChanged = changedIndex !== null && changedIndex !== undefined;
      const count = this.priority.length;
      let best = null;
      let bestScore = -1;
      this.data.variants.forEach((variant) => {
        if (hasChanged && !this.matches(variant, selected, [changedIndex])) return;
        let score = variant.available ? 1 : 0;
        this.priority.forEach((index, rank) => {
          if (index !== changedIndex && this.matches(variant, selected, [index])) score += Math.pow(2, count - rank);
        });
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    }

    /* Nearest available variant that keeps the option the shopper just picked
     * and every option ranked above it (color, then size, then version). Only
     * lower-ranked options change, so picking a color never changes the
     * shopper's size when that size is in stock in another version, and
     * picking a size never changes the color. Null when nothing qualifies. */
    availableRepair(selected, changedIndex) {
      const rank = this.priority.indexOf(changedIndex);
      const fixed = this.priority.slice(0, rank + 1);
      const count = this.priority.length;
      let best = null;
      let bestScore = -1;
      this.data.variants.forEach((variant) => {
        if (!variant.available || !this.matches(variant, selected, fixed)) return;
        let score = 0;
        this.priority.forEach((index, position) => {
          if (position > rank && this.matches(variant, selected, [index])) score += Math.pow(2, count - position);
        });
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    }

    /* A sold-out variant on load that the URL did not ask for (restored form
     * state, a stale default) is replaced by the first available one in
     * display order, keeping the color when it has stock. */
    preferAvailableOnLoad() {
      if (!this.variant || this.variant.available) return;
      if (/[?&]variant=/.test(window.location.search)) return;
      const selected = this.selectedOptions();
      const variant = this.availableRepair(selected, this.priority[0])
        || this.data.variants.find((candidate) => candidate.available);
      if (!variant) return;
      this.options.forEach((option) => this.checkOption(option.index, variant.options[option.index]));
      this.setVariant(variant, false);
    }

    checkOption(index, value) {
      const input = toArray(this.root.querySelectorAll(`input[data-lumos-pdp-option="${index}"]`)).find((candidate) => candidate.value === value);
      if (!input || input.checked) return;
      input.disabled = false;
      input.checked = true;
      this.silent = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.silent = false;
    }

    onChange(event) {
      const target = event.target;
      if (this.silent || !target || !target.matches || !target.matches('input[data-lumos-pdp-option]')) return;
      const changedIndex = Number(target.getAttribute('data-lumos-pdp-option'));
      const slideIndex = this.currentSlideIndex();
      const selected = this.selectedOptions();
      let variant = this.findVariant(selected);
      const repaired = !variant || !variant.available ? this.availableRepair(selected, changedIndex) : null;
      if (repaired) variant = repaired;
      else if (!variant) variant = this.repairVariant(selected, changedIndex);
      if (variant) {
        this.options.forEach((option) => {
          if (option.index !== changedIndex) this.checkOption(option.index, variant.options[option.index]);
        });
      }
      if (!variant) return;
      this.setVariant(variant, true);
      /* With mounts_jump the gallery opens the picked option's mounts slide. */
      const mountsIndex = this.data.mountsJump ? this.mountsIndex(variant.gallery) : -1;
      if (mountsIndex >= 0 && this.state.tab !== 'photos') this.selectTab('photos', false);
      this.showGallery(variant.gallery, mountsIndex >= 0 ? mountsIndex : slideIndex);
      if (mountsIndex >= 0) {
        const main = this.activeMain();
        if (main && main.closest('[data-lumos-pdp-gallery]') === this.groupFor(this.surface(), variant.gallery)) this.goTo(main, mountsIndex);
      }
    }

    /* Index of the mounts slide in the gallery of `key` on the current surface, -1 when it has none. */
    mountsIndex(key) {
      const main = this.mainOf(this.groupFor(this.surface(), key));
      if (!main) return -1;
      return this.slides(main).findIndex((slide) => slide.hasAttribute('data-lumos-pdp-mounts-slide'));
    }

    setVariant(variant, fromShopper) {
      this.variant = variant;
      if (this.variantInput && String(this.variantInput.value) !== String(variant.id)) {
        this.variantInput.value = variant.id;
        this.variantInput.setAttribute('value', variant.id);
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      toArray(this.root.querySelectorAll('shopify-payment-terms')).forEach((terms) => {
        terms.setAttribute('variant-id', variant.id);
      });
      if (fromShopper) this.updateUrl(variant.id);
      this.render();
    }

    updateUrl(variantId) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* Ignore URL errors in embedded editors. */
      }
    }

    /* ---------- setup + accessories ---------- */

    setupById(id) {
      return (this.data.setups || []).find((setup) => setup.id === id) || null;
    }

    isSetupAdded(id) {
      return this.state.setupIds.indexOf(id) !== -1;
    }

    currentSizeShort() {
      if (this.sizeIndex === null) return '';
      return shortSize(this.selectedOptions()[this.sizeIndex]);
    }

    /* Bundle pricing: the size slot whose aliases match the helmet size, else
     * the bundle product. Items pricing: every setup item, summed live. */
    setupVariant(setup) {
      if (!setup) return null;
      let variant = setup.variant || null;
      if (setup.pricing === 'items') {
        variant = this.itemsVariant(setup);
      } else if (setup.sized) {
        const size = this.currentSizeShort().toLowerCase();
        const slot = size ? (setup.sizes || []).find((candidate) => aliasList(candidate.match).indexOf(size) !== -1) : null;
        if (slot) variant = slot.variant || null;
      }
      /* compare_price (an MSRP total) replaces the compare-at price and only
       * shows while it is higher than the setup price. */
      const comparePrice = Number(setup.comparePrice) || 0;
      if (!variant || !comparePrice) return variant;
      return Object.assign({}, variant, { compareAt: comparePrice > Number(variant.price) ? comparePrice : 0 });
    }

    itemsVariant(setup) {
      const items = (setup.items || []).filter(Boolean);
      if (!items.length) return null;
      let price = 0;
      let reference = 0;
      let available = true;
      const lines = [];
      items.forEach((item) => {
        const variant = item.variant;
        if (!variant) {
          available = false;
          return;
        }
        const quantity = Math.max(1, Number(item.quantity) || 1);
        price += Number(variant.price || 0) * quantity;
        reference += Math.max(Number(variant.compareAt || 0), Number(variant.price || 0)) * quantity;
        if (!variant.available) available = false;
        lines.push({ id: Number(variant.id), quantity });
      });
      /* items_compare strikes through the items' compare-at total. */
      const compareAt = setup.itemsCompare && reference > price ? reference : 0;
      return { id: null, available: available && lines.length > 0, price, compareAt, image: setup.thumb || null, items: lines };
    }

    /* "10% OFF" from a price and its strike price (section setting auto_off_badge). */
    offBadge(price, compareAt) {
      const pattern = this.labels.offBadge || '';
      const was = Number(compareAt) || 0;
      if (!pattern || was <= Number(price)) return '';
      const percent = Math.round((was - Number(price)) * 100 / was);
      return percent > 0 ? pattern.replace('[percent]', String(percent)) : '';
    }

    accessoryById(id) {
      return (this.data.accessories || []).find((accessory) => accessory.id === id) || null;
    }

    accessoryAvailable(accessory) {
      return Boolean(accessory && accessory.variant && accessory.variant.available);
    }

    /* A card head only opens or closes the card (one open at a time). A setup
     * joins the purchase bar total only through its Add setup button: one
     * setup at a time, or several when the section allows multiple setups. */
    toggleSetupCard(id) {
      this.state.expandedId = this.state.expandedId === id ? null : id;
      this.render();
    }

    selectSetup(id) {
      const variant = this.setupVariant(this.setupById(id));
      if (!variant || !variant.available) return false;
      if (this.multiSetup) {
        const group = (this.setupById(id) || {}).group;
        if (group) this.state.setupIds = this.state.setupIds.filter((other) => other === id || (this.setupById(other) || {}).group !== group);
        if (!this.isSetupAdded(id)) this.state.setupIds = this.state.setupIds.concat(id);
      } else {
        this.state.setupIds = [id];
      }
      this.state.expandedId = id;
      this.render();
      return true;
    }

    removeSetup(id) {
      this.state.setupIds = this.state.setupIds.filter((other) => other !== id);
      this.render();
    }

    /* Drops added setups that are not available for the current size. */
    pruneSetups() {
      const size = this.currentSizeShort();
      this.state.setupIds.slice().forEach((id) => {
        const setup = this.setupById(id);
        const variant = this.setupVariant(setup);
        if (setup && variant && variant.available) return;
        this.state.setupIds = this.state.setupIds.filter((other) => other !== id);
        if (!this.multiSetup || this.state.expandedId === id) this.state.expandedId = null;
        if (setup) this.announce(fill(this.labels.setupRemoved, { setup: setup.name, size }));
      });
    }

    /* Accessory buttons in the media column add straight to the cart. The
     * button, the toast and the purchase bar show the pending state at once;
     * the drawer stays closed so it never slides over the purchase bar, and
     * the added accessories join the bar total. */
    quickAdd(key) {
      if (this.state.quickPending.size || this.state.pending) return Promise.resolve(null);
      const accessories = key === 'all' ? (this.data.accessories || []) : [this.accessoryById(key)];
      const list = accessories.filter((accessory) => this.accessoryAvailable(accessory));
      if (!list.length) return Promise.resolve(null);
      const items = list.map((accessory) => ({ id: Number(accessory.variant.id), quantity: 1 }));
      this.state.quickPending.add(key);
      this.renderExtras();
      this.renderBuyButtons();
      this.toast(this.labels.adding);
      return this.postCart(items, { source: 'lumos-pdp-accessory', variant: list[0].variant, keepDrawerClosed: true }).then((content) => {
        if (!content) return null;
        list.forEach((accessory) => this.state.extras.set(accessory.id, (this.state.extras.get(accessory.id) || 0) + 1));
        this.markQuickAdded(key);
        this.toast(this.labels.added);
        return content;
      }).finally(() => {
        this.state.quickPending.delete(key);
        this.renderExtras();
        this.renderSummary();
        this.renderBuyButtons();
      });
    }

    /* Accessories added from this page leave the bar total once the shopper
     * removes them from the cart (drawer quantity changes end with
     * theme:loading:end). */
    onCartSettled() {
      if (!this.state.extras.size) return;
      fetch(`${(window.routes && window.routes.cartUrl) || '/cart'}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) return;
          const inCart = new Map();
          cart.items.forEach((item) => inCart.set(String(item.variant_id), (inCart.get(String(item.variant_id)) || 0) + item.quantity));
          let changed = false;
          this.state.extras.forEach((quantity, id) => {
            const accessory = this.accessoryById(id);
            const left = accessory ? Math.min(quantity, inCart.get(String(accessory.variant.id)) || 0) : 0;
            if (left === quantity) return;
            changed = true;
            if (left > 0) this.state.extras.set(id, left);
            else this.state.extras.delete(id);
          });
          if (changed) this.renderSummary();
        })
        .catch(() => {});
    }

    markQuickAdded(key) {
      this.state.quickAdded.add(key);
      window.clearTimeout(this.quickTimers.get(key));
      this.quickTimers.set(key, window.setTimeout(() => {
        this.state.quickAdded.delete(key);
        this.quickTimers.delete(key);
        this.renderExtras();
      }, 2500));
    }

    /* ---------- rendering ---------- */

    render() {
      this.renderOptions();
      this.renderPrice();
      this.renderSetups();
      this.renderExtras();
      this.renderSummary();
      this.renderBuyButtons();
    }

    renderOptions() {
      const selected = this.selectedOptions();
      this.options.forEach((option) => {
        const index = option.index;
        const primary = option.role === 'color' || option.role === 'size';
        /* Color and size are never disabled: they show as sold out and the
         * other options are repaired. Version and other options are disabled
         * when no variant has the value with the current color and size. */
        const related = primary ? this.primaryIndexes : this.primaryIndexes.concat(index);
        toArray(this.root.querySelectorAll(`input[data-lumos-pdp-option="${index}"]`)).forEach((input) => {
          const probe = selected.slice();
          probe[index] = input.value;
          let candidate = this.findVariant(probe);
          if (!candidate) {
            candidate = this.data.variants.find((variant) => this.matches(variant, probe, related))
              || (primary ? this.data.variants.find((variant) => this.matches(variant, probe, [index])) : null)
              || null;
          }
          const exists = Boolean(candidate);
          /* A color or size value with no variant for the other primary value
           * stays clickable (the click repairs) but shows as sold out. */
          const offered = !primary || this.data.variants.some((variant) => this.matches(variant, probe, related));
          /* Sold out only when nothing left has this value with the options
           * above it: a color when none of its variants is available, a size
           * when the selected color has none left in that size; version and
           * other options keep the exact match with the current selection. */
          let unavailable = !exists || !offered || !candidate.available;
          if (option.role === 'color' || option.role === 'size') {
            const colorOption = this.options.find((other) => other.role === 'color');
            const upstream = option.role === 'size' && colorOption ? [colorOption.index, index] : [index];
            unavailable = !this.data.variants.some((variant) => variant.available && this.matches(variant, probe, upstream));
          }
          const disabled = !primary && !exists && !input.checked;
          input.disabled = disabled;
          const label = input.closest('label');
          if (label) {
            label.classList.toggle('is-disabled', disabled);
            label.classList.toggle('is-soldout', !disabled && unavailable);
            const text = label.querySelector('[data-lumos-pdp-soldout-text]');
            if (text) text.hidden = disabled || !unavailable;
            /* price_option: each choice shows its pack's price and compare-at price. */
            const chipPrice = label.querySelector('[data-lumos-pdp-chip-price]');
            if (chipPrice && candidate) {
              const now = chipPrice.querySelector('[data-lumos-pdp-chip-now]');
              const was = chipPrice.querySelector('[data-lumos-pdp-chip-was]');
              const showWas = Number(candidate.compareAt) > Number(candidate.price);
              if (now) now.textContent = this.money(candidate.price);
              if (was) {
                was.hidden = !showWas;
                was.textContent = showWas ? this.money(candidate.compareAt) : '';
              }
              const off = label.querySelector('[data-lumos-pdp-chip-off]');
              if (off) {
                off.textContent = showWas ? this.offBadge(candidate.price, candidate.compareAt) : '';
                off.hidden = !off.textContent;
              }
            }
          }
        });
        const valueTarget = this.root.querySelector(`[data-lumos-pdp-selected="${index}"]`);
        if (valueTarget) valueTarget.textContent = this.displayValue(index, selected[index]);
      });
    }

    renderPrice() {
      const variant = this.variant;
      const price = this.root.querySelector('[data-lumos-pdp-price]');
      const compare = this.root.querySelector('[data-lumos-pdp-compare]');
      if (price) price.textContent = this.money(variant.price);
      if (compare) {
        const showCompare = Number(variant.compareAt) > Number(variant.price);
        compare.hidden = !showCompare;
        compare.textContent = showCompare ? this.money(variant.compareAt) : '';
      }
      const priceOff = this.root.querySelector('[data-lumos-pdp-price-off]');
      if (priceOff) {
        priceOff.textContent = this.offBadge(variant.price, variant.compareAt);
        priceOff.hidden = !priceOff.textContent;
      }
      const helmetImage = variant.imageLarge || variant.image;
      if (helmetImage) {
        toArray(this.root.querySelectorAll('[data-lumos-pdp-helmet-image]')).forEach((image) => {
          if (image.getAttribute('src') !== helmetImage) image.setAttribute('src', helmetImage);
        });
      }
    }

    renderSetups() {
      this.pruneSetups();
      const size = this.currentSizeShort();
      const selectedValues = this.selectedOptions().filter(Boolean).map((value) => String(value).trim().toLowerCase());
      (this.data.setups || []).forEach((setup) => {
        const card = this.root.querySelector(`[data-lumos-pdp-setup="${setup.id}"]`);
        const variant = this.setupVariant(setup);
        const available = Boolean(variant && variant.available);
        const selected = this.isSetupAdded(setup.id);
        const expanded = this.state.expandedId === setup.id;

        if (card) {
          card.dataset.soldout = String(!available);
          card.dataset.selected = String(selected);
          const head = card.querySelector('[data-lumos-pdp-setup-pick]');
          if (head) head.setAttribute('aria-expanded', String(expanded));
          const status = card.querySelector('[data-lumos-pdp-setup-status]');
          if (status) status.textContent = selected ? `, ${this.labels.setupAdded || 'Added'}` : '';
          const body = card.querySelector('.lumos-pdp__card-body');
          if (body) body.hidden = !expanded;
          const soldOut = card.querySelector('[data-lumos-pdp-setup-soldout]');
          if (soldOut) soldOut.hidden = available;
          const sizeNote = card.querySelector('[data-lumos-pdp-size-note]');
          if (sizeNote) sizeNote.textContent = fill(this.labels.sizeNote, { size });
          const toggle = card.querySelector('[data-lumos-pdp-setup-toggle]');
          if (toggle) {
            toggle.disabled = !available;
            toggle.classList.toggle('lumos-pdp__btn--primary', !selected);
            toggle.classList.toggle('lumos-pdp__btn--outline', selected);
            if (selected) toggle.textContent = this.labels.removeSetup || 'Remove setup';
            else toggle.textContent = available ? (this.labels.addSetup || 'Add setup') : (this.labels.soldOut || 'Sold out');
          }
        }

        const warnValues = (setup.warnValues || []).map((value) => String(value).trim().toLowerCase()).filter(Boolean);
        const warnOn = !warnValues.length || warnValues.some((value) => selectedValues.indexOf(value) !== -1);
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-warn="${setup.id}"]`)).forEach((node) => {
          node.hidden = !warnOn;
        });

        const hasCompare = Boolean(variant && (!variant.items || setup.itemsCompare || setup.comparePrice) && Number(variant.compareAt) > Number(variant.price));
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-price="${setup.id}"]`)).forEach((node) => {
          node.textContent = variant ? this.money(variant.price) : '';
        });
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-compare="${setup.id}"]`)).forEach((node) => {
          node.hidden = !hasCompare;
          node.textContent = hasCompare ? this.money(variant.compareAt) : '';
        });
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-off-badge="${setup.id}"]`)).forEach((node) => {
          node.textContent = hasCompare ? this.offBadge(variant.price, variant.compareAt) : '';
          node.hidden = !node.textContent;
        });
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-save="${setup.id}"]`)).forEach((node) => {
          node.hidden = !hasCompare;
        });
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-save-amount="${setup.id}"]`)).forEach((node) => {
          node.textContent = hasCompare ? `${this.labels.save || 'Save'} ${this.money(variant.compareAt - variant.price)}` : '';
        });
        toArray(this.root.querySelectorAll(`[data-lumos-pdp-setup-choose="${setup.id}"]`)).forEach((button) => {
          if (selected) {
            button.textContent = this.labels.back || 'Back to setup';
            button.disabled = false;
          } else if (available) {
            button.textContent = `${this.labels.choose || 'Add'} ${setup.name} · ${this.money(variant.price)}`;
            button.disabled = false;
          } else {
            button.textContent = `${setup.name} · ${this.labels.soldOut || 'Sold out'}`;
            button.disabled = true;
          }
        });
      });
    }

    renderExtras() {
      const accessories = this.data.accessories || [];
      accessories.forEach((accessory) => {
        const button = this.root.querySelector(`[data-lumos-pdp-extra="${accessory.id}"]`);
        if (!button) return;
        const available = this.accessoryAvailable(accessory);
        const pending = this.state.quickPending.has(accessory.id);
        const locked = this.state.pending || this.state.quickPending.size > 0;
        const added = this.state.quickAdded.has(accessory.id);
        const priceText = available ? this.money(accessory.variant.price) : (this.labels.soldOut || 'Sold out');
        button.disabled = !available || locked;
        button.classList.toggle('is-added', added);
        button.setAttribute('aria-busy', String(pending));
        button.setAttribute('aria-label', added
          ? `${accessory.label}, ${this.labels.added || 'Added to cart'}`
          : fill(this.labels.addAccessory, { name: accessory.label, price: priceText }));
        const price = button.querySelector('[data-lumos-pdp-extra-price]');
        if (price) price.textContent = priceText;
      });
      const addAll = this.root.querySelector('[data-lumos-pdp-add-all]');
      if (addAll) {
        const availableExtras = accessories.filter((accessory) => this.accessoryAvailable(accessory));
        const pending = this.state.quickPending.has('all');
        const locked = this.state.pending || this.state.quickPending.size > 0;
        const added = this.state.quickAdded.has('all');
        addAll.classList.toggle('is-added', added);
        addAll.disabled = availableExtras.length === 0 || locked;
        addAll.setAttribute('aria-busy', String(pending));
        const label = addAll.querySelector('[data-lumos-pdp-add-all-label]');
        if (label) label.textContent = pending ? this.labels.adding : (added ? this.labels.allAdded : this.labels.addAll);
      }
    }

    /* Size shortened; other values use the section's option value labels. */
    displayValue(index, value) {
      if (!value) return '';
      if (index === this.sizeIndex) return shortSize(value);
      const label = this.valueLabels[String(value).trim().toLowerCase()];
      return label || value;
    }

    /* Selected values in role order, joined with " · ". */
    summaryText(selected) {
      /* Options hidden as single values (single_value_options: hide) stay out of the summary. */
      return this.priority
        .filter((index) => {
          const group = this.root.querySelector(`[data-lumos-pdp-group="${index}"]`);
          return !(group && group.hidden);
        })
        .map((index) => this.displayValue(index, selected[index])).filter(Boolean).join(' · ');
    }

    orderLines() {
      const selected = this.selectedOptions();
      const size = this.sizeIndex === null ? '' : shortSize(selected[this.sizeIndex]);
      const lines = [{
        kind: 'product',
        title: this.data.productTitle,
        sub: this.summaryText(selected),
        price: this.variant.price,
        compareAt: this.variant.compareAt,
        image: this.variant.image,
        cart: [{ id: Number(this.variant.id), quantity: 1 }]
      }];
      this.state.setupIds.forEach((id) => {
        const setup = this.setupById(id);
        const variant = this.setupVariant(setup);
        if (!setup || !variant || !variant.available) return;
        const summary = setup.summary || '';
        lines.push({
          kind: 'setup',
          id: setup.id,
          title: setup.name,
          sub: setup.sized && size ? `${summary}${summary ? ' · ' : ''}${size}` : summary,
          price: variant.price,
          compareAt: variant.compareAt,
          image: variant.image || setup.thumb,
          cart: variant.items ? variant.items : [{ id: Number(variant.id), quantity: 1 }]
        });
      });
      /* Already in the cart: counted in the total, never posted again. */
      this.state.extras.forEach((quantity, id) => {
        const accessory = this.accessoryById(id);
        if (!accessory || !accessory.variant || !quantity) return;
        lines.push({
          kind: 'accessory',
          id,
          title: accessory.label,
          sub: quantity > 1 ? `${this.labels.added} · ×${quantity}` : this.labels.added,
          price: Number(accessory.variant.price) * quantity,
          compareAt: Number(accessory.variant.compareAt || 0) * quantity,
          image: accessory.variant.image,
          cart: []
        });
      });
      return lines;
    }

    renderSummary() {
      const lines = this.orderLines();
      const total = lines.reduce((sum, line) => sum + Number(line.price || 0), 0);
      const totalNode = this.root.querySelector('[data-lumos-pdp-total]');
      if (totalNode) totalNode.textContent = this.money(total);

      const helmetLine = this.root.querySelector('[data-lumos-pdp-helmet-line]');
      if (helmetLine) helmetLine.textContent = lines[0].sub;

      const list = this.root.querySelector('[data-lumos-pdp-order-list]');
      if (list) {
        list.textContent = '';
        lines.forEach((line) => {
          const item = document.createElement('li');
          item.className = 'lumos-pdp__order-item';
          const thumb = document.createElement('span');
          thumb.className = 'lumos-pdp__order-thumb';
          if (line.image) {
            const image = document.createElement('img');
            image.src = line.image;
            image.alt = '';
            image.width = 64;
            image.height = 64;
            image.loading = 'lazy';
            thumb.appendChild(image);
          }
          const text = document.createElement('span');
          text.className = 'lumos-pdp__order-text';
          const title = document.createElement('span');
          title.className = 'lumos-pdp__order-name';
          title.textContent = line.title;
          const sub = document.createElement('span');
          sub.className = 'lumos-pdp__order-sub';
          sub.textContent = line.sub;
          text.append(title, sub);
          const prices = document.createElement('span');
          prices.className = 'lumos-pdp__order-prices';
          const now = document.createElement('span');
          now.className = 'lumos-pdp__order-price';
          now.textContent = this.money(line.price);
          prices.appendChild(now);
          if (Number(line.compareAt) > Number(line.price)) {
            const was = document.createElement('s');
            was.textContent = this.money(line.compareAt);
            prices.appendChild(was);
          }
          item.append(thumb, text, prices);
          if (line.kind === 'product' || line.kind === 'accessory') {
            const spacer = document.createElement('span');
            spacer.className = 'lumos-pdp__order-spacer';
            spacer.setAttribute('aria-hidden', 'true');
            item.appendChild(spacer);
          } else {
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'lumos-pdp__order-remove';
            remove.setAttribute('data-lumos-pdp-order-remove', `setup:${line.id}`);
            const label = `${this.labels.remove || 'Remove'} ${line.title}`;
            remove.setAttribute('aria-label', label);
            remove.title = label;
            remove.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>';
            item.appendChild(remove);
          }
          list.appendChild(item);
        });
      }

      const order = this.root.querySelector('.lumos-pdp__order');
      if (order) order.hidden = !this.state.orderOpen;
      this.root.classList.toggle('is-order-open', this.state.orderOpen);
      toArray(this.root.querySelectorAll('[data-lumos-pdp-order-toggle]')).forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(this.state.orderOpen));
        if (toggle.classList.contains('lumos-pdp__order-tab')) {
          const label = this.state.orderOpen ? this.labels.hideOrder : this.labels.showOrder;
          toggle.setAttribute('aria-label', label);
          toggle.title = label;
        }
      });
    }

    renderBuyButtons() {
      const available = Boolean(this.variant && this.variant.available);
      const pending = this.state.pending || this.state.quickPending.size > 0;
      const addButton = this.root.querySelector('[data-lumos-pdp-atc]');
      const buyButton = this.root.querySelector('[data-lumos-pdp-buynow]');
      if (addButton) {
        addButton.disabled = !available || pending;
        addButton.setAttribute('aria-busy', String(pending));
        addButton.textContent = !available ? this.labels.soldOut : (pending ? this.labels.adding : this.labels.addToCart);
      }
      if (buyButton) {
        buyButton.disabled = !available || pending;
        buyButton.textContent = this.labels.buyNow;
      }
      /* While a back-in-stock app button (Mailchimp .back-in-stock-btn) is
       * showing for the sold-out variant, the bar offers Notify instead of
       * Sold out / Buy now. Without the app the bar keeps Sold out. */
      const notifyButton = this.root.querySelector('[data-lumos-pdp-notify]');
      const notify = Boolean(notifyButton && !available && this.backInStock
        && !this.backInStock.classList.contains('back-in-stock-btn--hidden'));
      if (notifyButton) notifyButton.hidden = !notify;
      if (addButton) addButton.hidden = notify;
      if (buyButton) buyButton.hidden = notify;
    }

    /* The app button shows and hides itself after its script loads and on
     * every variant change, so follow its class instead of guessing stock. */
    watchBackInStock() {
      this.backInStock = this.root.querySelector('.back-in-stock-btn');
      if (!this.backInStock || !this.root.querySelector('[data-lumos-pdp-notify]')) return;
      /* The bar's Notify stands in for the app's own button, which stays in
       * the page for Notify to click but out of view. The theme editor keeps
       * it visible so the block can still be placed and styled. */
      if (!(window.Shopify && window.Shopify.designMode)) {
        const block = this.backInStock.closest('.shopify-app-block');
        if (block) block.hidden = true;
        const apps = block && block.parentElement;
        if (apps && apps.classList.contains('lumos-pdp__apps') && Array.from(apps.children).every((child) => child.hidden)) apps.hidden = true;
      }
      if (typeof MutationObserver === 'function') {
        this.backInStockObserver = new MutationObserver(() => this.renderBuyButtons());
        this.backInStockObserver.observe(this.backInStock, { attributes: true, attributeFilter: ['class'] });
      }
      this.renderBuyButtons();
    }

    renderEta() {
      const target = this.root.querySelector('[data-lumos-pdp-eta]');
      if (!target) return;
      const settings = this.data.settings || {};
      const formatter = dateFormatter();
      const format = (days) => {
        const date = new Date();
        date.setDate(date.getDate() + Number(days || 0));
        return formatter.format(date);
      };
      target.textContent = fill(this.labels.shipText, {
        ship: format(settings.shipDays === undefined ? 1 : settings.shipDays),
        arrival: format(settings.arrivalDays === undefined ? 7 : settings.arrivalDays)
      });
    }

    /* ---------- cart ---------- */

    cartItems() {
      const merged = new Map();
      this.orderLines().forEach((line) => {
        (line.cart || []).forEach((entry) => {
          const key = String(entry.id);
          merged.set(key, (merged.get(key) || 0) + (Number(entry.quantity) || 1));
        });
      });
      return Array.from(merged.entries()).map(([id, quantity]) => ({ id: Number(id), quantity }));
    }

    /* The theme serialises cart writes through window.LumosCartOperations
     * (assets/lumos-cart-operations.js). Take its cart lock for the POST so
     * a drawer quantity change or another form cannot write at the same
     * time, and release it before `product:added`, otherwise the theme's
     * cart re-render would be deferred by our own lock. */
    acquireCartLock() {
      const operations = window.LumosCartOperations;
      if (!operations || typeof operations.beginCartMutation !== 'function') return { ok: true, lock: null };
      if (!this.cartOwner) this.cartOwner = { element: this.root };
      const lock = operations.beginCartMutation(this.cartOwner, { type: 'lumos-pdp-add', source: 'lumos-pdp' });
      return { ok: Boolean(lock), lock };
    }

    releaseCartLock() {
      const operations = window.LumosCartOperations;
      if (this.cartLock && operations && typeof operations.finishCartOperation === 'function') {
        operations.finishCartOperation(this.cartLock);
      }
      this.cartLock = null;
    }

    /* One POST to /cart/add.js under the theme cart lock, then the theme's own
     * `product:added`. Resolves with the cart response, or null after showing
     * the error toast. */
    postCart(items, options) {
      const settings = options || {};
      const claim = this.acquireCartLock();
      if (!claim.ok) {
        this.toast(this.labels.busy || 'Your cart is updating. Please try again in a moment.', true);
        return Promise.resolve(null);
      }
      this.cartLock = claim.lock;
      const routes = window.routes || {};
      const url = `${routes.cartAddUrl || '/cart/add'}.js`;
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      }).then((response) => response.json().catch(() => ({})).then((content) => {
        this.releaseCartLock();
        if (!response.ok) {
          const error = new Error((content && (content.description || content.message)) || this.labels.addError);
          error.content = content;
          throw error;
        }
        /* Same flag as the theme's floating Buy now: refresh without opening the
         * drawer (Buy now, and accessory adds that must not cover the bar). */
        if (settings.buyNow || settings.keepDrawerClosed) window.closeDrawer = true;
        this.root.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: Object.assign({}, settings.variant || this.variant),
            quantity: 1,
            items,
            form: this.form,
            source: settings.source || 'lumos-pdp'
          }
        }));
        return content;
      })).catch((error) => {
        this.releaseCartLock();
        this.toast((error && error.message) || this.labels.addError, true);
        return null;
      }).finally(() => {
        this.releaseCartLock();
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
    }

    /* Back from checkout: a page restored from the back/forward cache keeps
     * what Buy now left behind (pending buttons, window.closeDrawer) and a
     * cart from before the add. Unlock the purchase buttons and let the theme
     * re-read the cart. */
    restoreFromCache() {
      this.state.pending = false;
      window.closeDrawer = false;
      this.releaseCartLock();
      this.renderBuyButtons();
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    addToCart(buyNow) {
      if (this.state.pending || !this.variant || !this.variant.available) return Promise.resolve(null);
      let navigating = false;
      this.state.pending = true;
      this.renderBuyButtons();
      return this.postCart(this.cartItems(), { buyNow, source: 'lumos-pdp' }).then((content) => {
        if (!content) return null;
        if (buyNow) {
          navigating = true;
          window.location.assign('/checkout');
          return content;
        }
        return this.waitForDrawer().then(() => {
          this.toast(this.labels.added);
          return content;
        });
      }).finally(() => {
        if (!navigating) {
          this.state.pending = false;
          this.renderBuyButtons();
        }
      });
    }

    /* Resolves once the theme has opened the cart drawer after an add, or
     * after DRAWER_WAIT_MS, so Add to cart keeps its Adding… state through the
     * cart re-render instead of looking idle until the drawer appears. */
    waitForDrawer() {
      const drawer = document.getElementById('sidebar-cart');
      if (!drawer) return Promise.resolve();
      return new Promise((resolve) => {
        let observer = null;
        let timer = null;
        const done = () => {
          if (observer) observer.disconnect();
          window.clearTimeout(timer);
          resolve();
        };
        if (drawer.getAttribute('aria-hidden') === 'false') {
          done();
          return;
        }
        observer = new MutationObserver(() => {
          if (drawer.getAttribute('aria-hidden') === 'false') done();
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
        timer = window.setTimeout(done, DRAWER_WAIT_MS);
      });
    }

    /* ---------- gallery ---------- */

    surface() {
      return this.mobileQuery && this.mobileQuery.matches ? 'mobile' : 'desktop';
    }

    groups(surface) {
      const container = this.root.querySelector(`[data-lumos-pdp-surface="${surface}"]`);
      return container ? toArray(container.querySelectorAll('[data-lumos-pdp-gallery]')) : [];
    }

    groupFor(surface, key) {
      if (key === null || key === undefined) return null;
      const wanted = String(key);
      return this.groups(surface).find((group) => group.getAttribute('data-lumos-pdp-gallery') === wanted) || null;
    }

    visibleGroup(surface) {
      return this.groups(surface).find((group) => !group.hidden) || null;
    }

    mainOf(group) {
      return group ? group.querySelector('[data-lumos-pdp-main]') : null;
    }

    activeMain() {
      return this.mainOf(this.visibleGroup(this.surface()));
    }

    currentSlideIndex() {
      const main = this.activeMain();
      return main ? this.currentIndex(main) : 0;
    }

    slides(main) {
      return toArray(main.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide'));
    }

    currentIndex(main) {
      if (main.swiper && !main.swiper.destroyed) return main.swiper.activeIndex || 0;
      const wrapper = main.querySelector(':scope > .swiper-wrapper');
      if (!wrapper || !wrapper.clientWidth) return 0;
      return Math.round(wrapper.scrollLeft / wrapper.clientWidth);
    }

    /* Shows the group of `key` on one surface and hides the others (their
     * Swipers are destroyed). Leaves the surface alone when the key has no
     * group, so a missing gallery never blanks a valid one. */
    toggleGroups(surface, key) {
      const target = this.groupFor(surface, key);
      if (!target) return null;
      this.groups(surface).forEach((group) => {
        if (group !== target && !group.hidden) this.destroySwiper(this.mainOf(group));
        group.hidden = group !== target;
      });
      return target;
    }

    initGallery() {
      this.gallery.key = this.variant ? this.variant.gallery : null;
      ['desktop', 'mobile'].forEach((surface) => this.toggleGroups(surface, this.gallery.key));
      this.mountSurface(this.surface(), 0);
    }

    /* Creates the Swiper for the visible group of the current surface only;
     * the other surface is display:none and gets its Swiper on breakpoint change. */
    mountSurface(surface, index) {
      this.gallery.surface = surface;
      const main = this.mainOf(this.visibleGroup(surface));
      if (!main) return;
      this.createSwiper(main, surface, index);
      this.updateGallery(main);
    }

    createSwiper(main, surface, index) {
      const count = this.slides(main).length;
      const target = Math.max(0, Math.min(count - 1, Number(index) || 0));
      if (typeof window.Swiper === 'function') {
        if (!main.swiper || main.swiper.destroyed) {
          try {
            const swiper = new window.Swiper(main, Object.assign({}, SWIPER_OPTIONS[surface] || SWIPER_OPTIONS.desktop));
            if (typeof swiper.on === 'function') swiper.on('slideChange', () => this.updateGallery(main));
          } catch (error) {
            console.error('[lumos-pdp] Unable to create the gallery', error);
          }
        }
        const swiper = main.swiper;
        if (swiper && !swiper.destroyed) {
          if (typeof swiper.update === 'function') swiper.update();
          if (swiper.activeIndex !== target) swiper.slideTo(target, 0);
          return;
        }
      }
      /* Native scroll-snap fallback (assets/lumos-pdp.css). */
      this.bindScroll(main);
      const wrapper = main.querySelector(':scope > .swiper-wrapper');
      if (wrapper) wrapper.scrollLeft = target * wrapper.clientWidth;
    }

    destroySwiper(main) {
      if (!main || !main.swiper) return;
      try {
        if (!main.swiper.destroyed && typeof main.swiper.destroy === 'function') main.swiper.destroy(true, true);
      } catch (error) {
        /* Ignore teardown errors from a half-created Swiper. */
      }
      try {
        delete main.swiper;
      } catch (error) {
        main.swiper = null;
      }
    }

    bindScroll(main) {
      const wrapper = main.querySelector(':scope > .swiper-wrapper');
      if (!wrapper || wrapper.lumosPdpScroll) return;
      wrapper.lumosPdpScroll = true;
      wrapper.addEventListener('scroll', () => {
        if (!main.swiper) this.updateGallery(main);
      }, { passive: true });
    }

    /* Resolves once the image is loaded and decoded, broken, or after
     * IMAGE_WAIT_MS, so a slow image never blocks the variant switch. */
    waitForImage(image) {
      if (!image) return Promise.resolve();
      const wasLazy = image.getAttribute('loading') === 'lazy';
      if (wasLazy) image.setAttribute('loading', 'eager');
      return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        let onLoad = null;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', finish);
          resolve();
        };
        onLoad = () => {
          if (typeof image.decode === 'function') image.decode().then(finish, finish);
          else finish();
        };
        timer = window.setTimeout(finish, IMAGE_WAIT_MS);
        if (image.complete && image.naturalWidth > 0) {
          onLoad();
          return;
        }
        if (image.complete && !wasLazy) {
          finish();
          return;
        }
        image.addEventListener('load', onLoad);
        image.addEventListener('error', finish);
      });
    }

    /* Keeps the shopper on the same slide (front, lifestyle, in the box, ...)
     * when the gallery key changes. Variants that differ only by size share a
     * group, so a size change keeps the gallery as it is. */
    showGallery(key, keptIndex) {
      const token = this.gallery.token + 1;
      this.gallery.token = token;
      if (key === null || key === undefined) return;
      this.gallery.key = key;
      const surface = this.surface();
      this.toggleGroups(surface === 'mobile' ? 'desktop' : 'mobile', key);
      const current = this.visibleGroup(surface);
      const target = this.groupFor(surface, key);
      if (!target || target === current) return;
      const main = this.mainOf(target);
      const slides = main ? this.slides(main) : [];
      const index = Math.max(0, Math.min(slides.length - 1, Number(keptIndex) || 0));
      const slide = slides[index];
      const image = slide ? (slide.tagName === 'IMG' ? slide : slide.querySelector('img')) : null;
      this.waitForImage(image).then(() => {
        if (token !== this.gallery.token || this.surface() !== surface || !this.root.isConnected) return;
        this.toggleGroups(surface, key);
        if (!main) return;
        this.createSwiper(main, surface, index);
        this.updateGallery(main);
      });
    }

    onSurfaceChange() {
      const previous = this.gallery.surface;
      const previousMain = previous ? this.mainOf(this.visibleGroup(previous)) : null;
      const index = previousMain ? this.currentIndex(previousMain) : 0;
      this.gallery.token += 1;
      ['desktop', 'mobile'].forEach((surface) => {
        this.destroySwiper(this.mainOf(this.visibleGroup(surface)));
        this.toggleGroups(surface, this.gallery.key);
      });
      this.mountSurface(this.surface(), index);
      this.measure();
    }

    goTo(main, index) {
      const count = this.slides(main).length;
      const target = Math.max(0, Math.min(count - 1, index));
      if (main.swiper && !main.swiper.destroyed) {
        main.swiper.slideTo(target);
      } else {
        const wrapper = main.querySelector(':scope > .swiper-wrapper');
        if (wrapper) wrapper.scrollTo({ left: target * wrapper.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        window.setTimeout(() => this.updateGallery(main), 350);
      }
    }

    updateGallery(main) {
      const group = main.closest('[data-lumos-pdp-gallery]');
      if (!group) return;
      const count = this.slides(main).length;
      const index = Math.max(0, Math.min(count - 1, this.currentIndex(main)));
      toArray(group.querySelectorAll('[data-lumos-pdp-goto]')).forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      toArray(group.querySelectorAll('.lumos-pdp__dot')).forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
      const previous = group.querySelector('[data-lumos-pdp-prev]');
      const next = group.querySelector('[data-lumos-pdp-next]');
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= count - 1;
      const container = group.closest('[data-lumos-pdp-surface]');
      if (container && container.getAttribute('data-lumos-pdp-surface') === 'mobile') this.renderMobileDots(count, index);
    }

    renderMobileDots(count, index) {
      const container = this.root.querySelector('[data-lumos-pdp-mobile-dots]');
      if (!container) return;
      if (count < 2) {
        container.textContent = '';
        container.hidden = true;
        return;
      }
      container.hidden = false;
      if (container.children.length !== count) {
        container.textContent = '';
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lumos-pdp__mdot';
          dot.setAttribute('data-lumos-pdp-mdot', String(i));
          dot.setAttribute('aria-label', fill(this.labels.imageCount, { n: i + 1, total: count }));
          dot.appendChild(document.createElement('span'));
          container.appendChild(dot);
        }
      }
      toArray(container.children).forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* ---------- media tabs ---------- */

    selectTab(name, moveFocus) {
      this.state.tab = name;
      toArray(this.root.querySelectorAll('[data-lumos-pdp-tab]')).forEach((tab) => {
        const active = tab.getAttribute('data-lumos-pdp-tab') === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && moveFocus) tab.focus();
      });
      toArray(this.root.querySelectorAll('[data-lumos-pdp-panel]')).forEach((panel) => {
        const panelName = panel.getAttribute('data-lumos-pdp-panel');
        const active = panelName === name;
        if (panelName === 'photos') {
          /* Keep the photo panel laid out so Swiper keeps its measurements. */
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', String(!active));
          if (active) panel.removeAttribute('inert');
          else panel.setAttribute('inert', '');
        } else {
          panel.hidden = !active;
        }
      });
      if (name !== 'intro') {
        toArray(this.root.querySelectorAll('[data-lumos-pdp-video]')).forEach((video) => {
          if (!video.paused) video.pause();
        });
      }
      if (name === 'photos') {
        const main = this.activeMain();
        if (main && main.swiper && typeof main.swiper.update === 'function') main.swiper.update();
      }
    }

    /* ---------- zoom ---------- */

    openZoom(image) {
      const main = image.closest('[data-lumos-pdp-main]');
      const surface = image.closest('[data-lumos-pdp-zoom-enabled]');
      if (!main || (surface && surface.getAttribute('data-lumos-pdp-zoom-enabled') === 'false')) return;
      this.zoom.images = toArray(main.querySelectorAll('img[data-lumos-pdp-zoom]'));
      this.zoom.index = Math.max(0, this.zoom.images.indexOf(image));
      this.renderZoom();
      this.openModal('zoom', image);
    }

    renderZoom() {
      const modal = this.root.querySelector('[data-lumos-pdp-modal="zoom"]');
      const current = this.zoom.images[this.zoom.index];
      if (!modal || !current) return;
      const target = modal.querySelector('[data-lumos-pdp-zoom-img]');
      target.src = current.getAttribute('data-lumos-pdp-zoom') || current.currentSrc || current.src;
      target.alt = current.alt || '';
      /* Product photos keep their gallery size (product_image_scale) in the dialog. */
      const scale = current.classList.contains('lumos-pdp__slide--product')
        ? parseFloat(getComputedStyle(current).getPropertyValue('--lumos-pdp-product-scale'))
        : 1;
      if (scale > 0 && scale < 1) target.style.setProperty('--lumos-pdp-zoom-scale', String(scale));
      else target.style.removeProperty('--lumos-pdp-zoom-scale');
      const counter = modal.querySelector('[data-lumos-pdp-zoom-count]');
      if (counter) counter.textContent = `${this.zoom.index + 1} / ${this.zoom.images.length}`;
      toArray(modal.querySelectorAll('[data-lumos-pdp-zoom-step]')).forEach((button) => {
        button.hidden = this.zoom.images.length < 2;
      });
    }

    stepZoom(direction) {
      const count = this.zoom.images.length;
      if (count < 2) return;
      this.zoom.index = (this.zoom.index + direction + count) % count;
      this.renderZoom();
    }

    /* ---------- dialogs ---------- */

    openModal(name, trigger) {
      const modal = this.root.querySelector(`[data-lumos-pdp-modal="${name}"]`);
      if (!modal) return;
      if (this.openModalElement && this.openModalElement !== modal) this.closeModal(false);
      this.modalTrigger = trigger || document.activeElement;
      modal.hidden = false;
      this.openModalElement = modal;
      document.documentElement.classList.add('lumos-pdp-modal-open');
      const dialog = modal.querySelector('[role="dialog"]') || modal;
      const focusables = focusableWithin(dialog);
      window.requestAnimationFrame(() => (focusables[0] || dialog).focus());
    }

    closeModal(restoreFocus) {
      const modal = this.openModalElement;
      if (!modal) return;
      modal.hidden = true;
      this.openModalElement = null;
      document.documentElement.classList.remove('lumos-pdp-modal-open');
      if (restoreFocus !== false && this.modalTrigger && typeof this.modalTrigger.focus === 'function' && document.contains(this.modalTrigger)) {
        this.modalTrigger.focus();
      }
      this.modalTrigger = null;
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;
      const hit = (selector) => target.closest(selector);
      let node;

      if ((node = hit('[data-lumos-pdp-close]'))) {
        event.preventDefault();
        this.closeModal(true);
        return;
      }
      if ((node = hit('[data-lumos-pdp-notify]'))) {
        if (this.backInStock) this.backInStock.click();
        return;
      }
      if ((node = hit('[data-lumos-pdp-zoom-step]'))) {
        this.stepZoom(Number(node.getAttribute('data-lumos-pdp-zoom-step')) || 1);
        return;
      }
      if ((node = hit('[data-lumos-pdp-open]'))) {
        event.preventDefault();
        this.openModal(node.getAttribute('data-lumos-pdp-open'), node);
        return;
      }
      if ((node = hit('[data-lumos-pdp-tab]'))) {
        this.selectTab(node.getAttribute('data-lumos-pdp-tab'), false);
        return;
      }
      if ((node = hit('[data-lumos-pdp-prev]')) || (node = hit('[data-lumos-pdp-next]'))) {
        const main = this.mainOf(node.closest('[data-lumos-pdp-gallery]'));
        if (main) this.goTo(main, this.currentIndex(main) + (node.hasAttribute('data-lumos-pdp-next') ? 1 : -1));
        return;
      }
      if ((node = hit('[data-lumos-pdp-goto]'))) {
        const main = this.mainOf(node.closest('[data-lumos-pdp-gallery]'));
        if (main) this.goTo(main, Number(node.getAttribute('data-lumos-pdp-goto')) || 0);
        return;
      }
      if ((node = hit('[data-lumos-pdp-mdot]'))) {
        const main = this.activeMain();
        if (main) this.goTo(main, Number(node.getAttribute('data-lumos-pdp-mdot')) || 0);
        return;
      }
      if ((node = hit('img[data-lumos-pdp-zoom]'))) {
        this.openZoom(node);
        return;
      }
      if ((node = hit('[data-lumos-pdp-accordion]'))) {
        const panel = document.getElementById(node.getAttribute('aria-controls'));
        const expanded = node.getAttribute('aria-expanded') === 'true';
        node.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
        return;
      }
      if ((node = hit('[data-lumos-pdp-setup-pick]'))) {
        this.toggleSetupCard(node.getAttribute('data-lumos-pdp-setup-pick'));
        return;
      }
      if ((node = hit('[data-lumos-pdp-setup-toggle]'))) {
        const id = node.getAttribute('data-lumos-pdp-setup-toggle');
        if (this.isSetupAdded(id)) this.removeSetup(id);
        else this.selectSetup(id);
        return;
      }
      if ((node = hit('[data-lumos-pdp-setup-choose]'))) {
        const id = node.getAttribute('data-lumos-pdp-setup-choose');
        if (!this.isSetupAdded(id)) this.selectSetup(id);
        this.closeModal(true);
        return;
      }
      if ((node = hit('[data-lumos-pdp-extra]'))) {
        this.quickAdd(node.getAttribute('data-lumos-pdp-extra'));
        return;
      }
      if ((node = hit('[data-lumos-pdp-add-all]'))) {
        this.quickAdd('all');
        return;
      }
      if ((node = hit('[data-lumos-pdp-order-remove]'))) {
        const value = node.getAttribute('data-lumos-pdp-order-remove') || '';
        if (value.indexOf('setup:') === 0) this.removeSetup(value.slice(6));
        else this.render();
        const toggle = this.root.querySelector('.lumos-pdp__order-tab');
        if (toggle) toggle.focus();
        return;
      }
      if ((node = hit('[data-lumos-pdp-order-toggle]'))) {
        this.state.orderOpen = !this.state.orderOpen;
        this.renderSummary();
        return;
      }
      if ((node = hit('[data-lumos-pdp-atc]'))) {
        this.addToCart(false);
        return;
      }
      if ((node = hit('[data-lumos-pdp-buynow]'))) {
        this.addToCart(true);
        return;
      }
      if ((node = hit('[data-lumos-pdp-totop]'))) {
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    onKeydown(event) {
      const modal = this.openModalElement;
      if (modal) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeModal(true);
          return;
        }
        if (modal.getAttribute('data-lumos-pdp-modal') === 'zoom' && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
          this.stepZoom(event.key === 'ArrowRight' ? 1 : -1);
          return;
        }
        if (event.key === 'Tab') {
          const dialog = modal.querySelector('[role="dialog"]') || modal;
          const focusables = focusableWithin(dialog);
          if (!focusables.length) {
            event.preventDefault();
            dialog.focus();
            return;
          }
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
            event.preventDefault();
            first.focus();
          }
        }
        return;
      }
      const tab = event.target && event.target.closest ? event.target.closest('[data-lumos-pdp-tab]') : null;
      if (tab && this.root.contains(tab) && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) !== -1) {
        event.preventDefault();
        const tabs = toArray(this.root.querySelectorAll('[data-lumos-pdp-tab]'));
        const index = tabs.indexOf(tab);
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        this.selectTab(tabs[next].getAttribute('data-lumos-pdp-tab'), true);
      }
    }

    /* ---------- layout ---------- */

    /* Height of the sticky header that currently covers the top of the
     * viewport. The theme hides the header while scrolling down
     * (.minimised, sections/header.liquid), so it is 0 then. */
    headerOffset() {
      const header = this.header || (this.header = document.querySelector('.shopify-section--header'));
      if (!header || header.classList.contains('minimised')) return 0;
      const position = window.getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      const rect = header.getBoundingClientRect();
      return Math.max(0, Math.min(Math.round(rect.height), Math.round(rect.bottom)));
    }

    /* Full header height, used to size the gallery for the first screen. */
    headerHeight() {
      const header = this.header || (this.header = document.querySelector('.shopify-section--header'));
      return header ? Math.round(header.getBoundingClientRect().height) : 0;
    }

    syncStickyTop() {
      const stickyTop = this.headerOffset();
      if (stickyTop === this.stickyTop) return;
      this.stickyTop = stickyTop;
      this.root.style.setProperty('--lumos-pdp-sticky-top', `${stickyTop}px`);
    }

    /* The header slides in and out with a transform transition, so the offset
     * read when its class changes is the starting position. Read it again
     * once the slide has ended. */
    onHeaderTransitionEnd(event) {
      if (event.target === this.header) this.syncStickyTop();
    }

    measure() {
      const firstTop = Math.max(0, Math.round(this.root.getBoundingClientRect().top + window.pageYOffset));
      const barHeight = this.bar ? Math.round(this.bar.getBoundingClientRect().height) : 0;
      this.root.style.setProperty('--lumos-pdp-first-top', `${firstTop}px`);
      this.root.style.setProperty('--lumos-pdp-header-h', `${this.headerHeight()}px`);
      document.documentElement.style.setProperty('--lumos-pdp-bar-h', `${barHeight}px`);
      document.body.classList.add('lumos-pdp-has-bar');
      this.firstTop = firstTop;
      this.stickyTop = null;
      this.syncStickyTop();
      if (!this.headerObserver && this.header && typeof MutationObserver === 'function') {
        this.headerObserver = new MutationObserver(() => this.syncStickyTop());
        this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class', 'style'] });
        this.header.addEventListener('transitionend', this.onHeaderTransitionEnd);
      }
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.measure();
        this.updateScrollState();
      }, 120);
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateScrollState();
      });
    }

    updateScrollState() {
      this.syncStickyTop();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const pastFirstScreen = scrollY > window.innerHeight;
      const toTop = this.root.querySelector('[data-lumos-pdp-totop]');
      if (toTop) {
        toTop.classList.toggle('is-visible', pastFirstScreen);
        toTop.tabIndex = pastFirstScreen ? 0 : -1;
        toTop.setAttribute('aria-hidden', String(!pastFirstScreen));
      }
      this.root.classList.toggle('is-scrolled', scrollY > (this.firstTop || 0) + 8);
    }
  }

  /* Card images: every product is scaled to the same share of its card, read
   * from the image's transparent pixels, so a tightly cropped image (a light
   * filling the frame) and a loosely cropped one (a helmet with margins) show
   * at the same size. Opaque or unreadable (cross-origin) images keep their
   * own framing. */
  const CARD_FILL_WIDTH = 0.72;
  const CARD_FILL_HEIGHT = 0.74;
  const CARD_SAMPLE = 96;
  const CARD_ALPHA = 16;

  function cardProductBox(image) {
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    if (!naturalWidth || !naturalHeight) return null;
    const canvas = document.createElement('canvas');
    canvas.width = CARD_SAMPLE;
    canvas.height = CARD_SAMPLE;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    /* Drawn like object-fit: contain, so the box is relative to the img element. */
    const scale = Math.min(CARD_SAMPLE / naturalWidth, CARD_SAMPLE / naturalHeight);
    const drawWidth = Math.max(1, Math.round(naturalWidth * scale));
    const drawHeight = Math.max(1, Math.round(naturalHeight * scale));
    const offsetX = Math.floor((CARD_SAMPLE - drawWidth) / 2);
    const offsetY = Math.floor((CARD_SAMPLE - drawHeight) / 2);
    let pixels;
    try {
      context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
      pixels = context.getImageData(0, 0, CARD_SAMPLE, CARD_SAMPLE).data;
    } catch (error) {
      return null;
    }
    const alphaAt = (x, y) => pixels[(y * CARD_SAMPLE + x) * 4 + 3];
    const right = offsetX + drawWidth - 1;
    const bottom = offsetY + drawHeight - 1;
    if ([alphaAt(offsetX, offsetY), alphaAt(right, offsetY), alphaAt(offsetX, bottom), alphaAt(right, bottom)].some((alpha) => alpha > CARD_ALPHA)) return null;
    let minX = CARD_SAMPLE;
    let minY = CARD_SAMPLE;
    let maxX = -1;
    let maxY = -1;
    for (let y = offsetY; y <= bottom; y += 1) {
      for (let x = offsetX; x <= right; x += 1) {
        if (alphaAt(x, y) > CARD_ALPHA) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { left: minX / CARD_SAMPLE, top: minY / CARD_SAMPLE, right: (maxX + 1) / CARD_SAMPLE, bottom: (maxY + 1) / CARD_SAMPLE };
  }

  function fitCardImage(image) {
    const box = cardProductBox(image);
    if (!box) {
      image.style.removeProperty('transform');
      return;
    }
    const width = box.right - box.left;
    const height = box.bottom - box.top;
    const scale = Math.min(2, Math.max(0.5, Math.min(CARD_FILL_WIDTH / width, CARD_FILL_HEIGHT / height)));
    const shiftX = (0.5 - (box.left + box.right) / 2) * scale * 100;
    const shiftY = (0.5 - (box.top + box.bottom) / 2) * scale * 100;
    image.style.transform = `translate(${shiftX.toFixed(2)}%, ${shiftY.toFixed(2)}%) scale(${scale.toFixed(3)})`;
  }

  /* You May Also Like carousel (native horizontal scroll + arrow buttons). */
  class LumosPdpCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-lumos-pdp-ymal-track]');
      this.previous = root.querySelector('[data-lumos-pdp-ymal-prev]');
      this.next = root.querySelector('[data-lumos-pdp-ymal-next]');
      if (!this.track) return;
      this.onImageLoad = (event) => fitCardImage(event.currentTarget);
      this.images = toArray(this.track.querySelectorAll('.lumos-pdp-ymal__media img'));
      this.images.forEach((image) => {
        image.addEventListener('load', this.onImageLoad);
        if (image.complete && image.naturalWidth) fitCardImage(image);
      });
      this.update = this.update.bind(this);
      this.onClick = (event) => {
        const button = event.target.closest && event.target.closest('[data-lumos-pdp-ymal-prev], [data-lumos-pdp-ymal-next]');
        if (!button) return;
        const card = this.track.querySelector('li');
        const gap = parseFloat(window.getComputedStyle(this.track).columnGap || '16') || 16;
        const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: button.hasAttribute('data-lumos-pdp-ymal-next') ? step : -step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      };
      root.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update, { passive: true });
      this.update();
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      this.root.classList.toggle('is-scrollable', max > 0);
    }

    destroy() {
      if (!this.track) return;
      this.images.forEach((image) => image.removeEventListener('load', this.onImageLoad));
      this.root.removeEventListener('click', this.onClick);
      this.track.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.update);
    }
  }

  function initialize(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    const roots = toArray(context.querySelectorAll('[data-lumos-pdp-root]'));
    if (context.matches && context.matches('[data-lumos-pdp-root]')) roots.push(context);
    roots.forEach((root) => {
      if (products.has(root)) return;
      const instance = new LumosPdpProduct(root);
      if (instance.ready) products.set(root, instance);
    });
    const ymalRoots = toArray(context.querySelectorAll('[data-lumos-pdp-ymal]'));
    if (context.matches && context.matches('[data-lumos-pdp-ymal]')) ymalRoots.push(context);
    ymalRoots.forEach((root) => {
      if (!carousels.has(root)) carousels.set(root, new LumosPdpCarousel(root));
    });
  }

  function destroyWithin(scope) {
    [products, carousels].forEach((registry) => {
      registry.forEach((instance, root) => {
        if (root === scope || (scope.contains && scope.contains(root))) {
          instance.destroy();
          registry.delete(root);
        }
      });
    });
  }

  window.LumosPdp = { initialize, formatMoney };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
